import cn from 'classnames'
import {FC} from 'react'

import {Button, ButtonStatus} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {TextFontWeight} from '../../content/Text'

type ModalFooterButton = {
  type?: 'button' | 'submit'
  text?: string
  onClick?: () => void
  size?: ButtonSize
  fullWidth?: boolean
  isDisabled?: boolean
}

type ModalFooterProps = {
  primaryButton?: ModalFooterButton
  secondaryButton?: ModalFooterButton
  isSubmitting?: boolean
  status?: ButtonStatus
  orientation?: 'horizontal' | 'vertical'
  isCentered?: boolean
}

export const ModalFooter: FC<ModalFooterProps> = ({
  primaryButton,
  secondaryButton,
  isSubmitting,
  status,
  orientation = 'vertical',
  isCentered = false,
}) => {
  const {t} = useTranslation()
  const {saveButton, cancelButton} = useCommonContent()
  const isHorizontal = orientation === 'horizontal'

  const primaryButtonSize = primaryButton?.size ?? ButtonSize.small

  return (
    <>
      <div className={cn('buttons', isHorizontal && 'horizontal', isCentered && 'centered')}>
        {primaryButton && (
          <div className="button">
            <Button
              type={primaryButton.type ?? 'submit'}
              variant={ButtonVariant.primary}
              size={primaryButtonSize}
              disabled={primaryButton.isDisabled || isSubmitting}
              onClick={primaryButton.onClick}
              fullWidth={primaryButton.fullWidth ?? true}
              status={status ?? (isSubmitting ? ButtonStatus.submitting : ButtonStatus.idle)}
            >
              {primaryButton.text || t(saveButton)}
            </Button>
          </div>
        )}
        {secondaryButton && (
          <div className="button">
            <Button
              variant={ButtonVariant.tertiary}
              size={secondaryButton.size ?? ButtonSize.small}
              type={secondaryButton.type ?? 'button'}
              disabled={secondaryButton.isDisabled || isSubmitting}
              onClick={secondaryButton.onClick}
              fontWeight={TextFontWeight.medium}
              fullWidth={secondaryButton.fullWidth ?? true}
            >
              {secondaryButton.text || t(cancelButton)}
            </Button>
          </div>
        )}
      </div>

      <style jsx>{`
        .buttons {
          margin-top: ${theme.spacing[6]};
        }
        .button + .button {
          margin-top: ${theme.spacing[2]};
        }
        .horizontal {
          display: flex;
          justify-content: flex-start;
          flex-direction: row-reverse;
        }
        .horizontal .button + .button {
          margin-top: 0;
          margin-right: ${theme.spacing[2]};
        }
        .centered {
          text-align: center;
        }
        @media ${MOBILE_SCREENSIZE} {
          .buttons {
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}
